<template>
  <div
    v-if="currentChannel"
    :class="{
      chat: true,
      'chat-public': !isPrivate,
      'chat-private': isPrivate
    }"
    :data-has-tags="hasTags"
  >
    <div v-if="showMessages" class="chat__container">
      <div v-if="shouldShowCallout" class="chat__container--callout">
        <CallOut
          :title="calloutMessage.title"
          :description="calloutMessage.description"
          title-icon="icAlertCircleOutlined"
          dismissible
          @on-close="handleCloseCallout"
        />
      </div>
      <CChannelMessages
        v-if="currentChannel.id"
        ref="messages"
        :current-channel="currentChannel"
        class="chat__messages"
        :is-private="isPrivate"
        @on-internal-redirect="$emit('onInternalRedirect', $event)"
      />

      <CWarningAlert
        v-if="nextFollowUp"
        v-show="!isPrivate"
        icon="icCalendar"
        class="chat-messages__next-follow-up"
        :title="nextFollowUp.title"
        :tooltip="nextFollowUp.tooltip"
      />

      <CChannelActionButtons
        :channel="currentChannel"
        :disabled="isClosingChannel"
        @on-start-videocall="startVideocall"
        @on-close-channel="closeChannel"
      />

      <CWriteBox
        class="chat__write-box"
        :channel="currentChannel"
        :is-private="isPrivate"
        :disabled="disableChatEditor"
        @is-private="changeChannel"
        @message-sent="focusMessages"
      />
    </div>
    <div v-else class="channel-messages__empty">Carregando...</div>
  </div>
  <div v-else class="error">
    <div class="message">Esse canal não existe</div>
  </div>
</template>

<script>
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { mapGetters, mapState } from 'vuex'

import { CWarningAlert } from '@/components/channels/atoms'
import CWriteBox from '@/components/channels/molecules/CWriteBox.vue'
import { CChannelMessages } from '@/components/channels/organisms'
import { CallOut } from '@/components/shared/atoms'
import { CHANNEL_SUBCATEGORIES, ChannelStatus } from '@/models/channels'
import { screeningNavigationStatus } from '@/models/screeningNavigation'

import CChannelActionButtons from '../molecules/CChannelActionButtons.vue'

export default {
  name: 'CChat',
  components: {
    CWriteBox,
    CWarningAlert,
    CChannelMessages,
    CChannelActionButtons,
    CallOut
  },
  inject: ['pillarId'],
  emits: ['onInternalRedirect'],
  data() {
    return {
      currentUser: undefined,
      stopChannelListener: undefined,
      isCalloutClosed: true,
      isClosingChannel: false
    }
  },
  computed: {
    ...mapState('ui/channel', ['showSearchMessage']),
    ...mapState('pillars/channel/message', ['isPrivate']),
    ...mapGetters('pillars/channel', ['currentChannel']),
    ...mapState('pillars/channel', ['healthTags']),
    ...mapGetters('healthEvents', ['plannedFollowUps']),
    disableChatEditor() {
      return (
        this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.VIRTUAL_CLINIC
      )
    },
    nextFollowUp() {
      if (this.plannedFollowUps.length) {
        const [nextFollowUp] = this.plannedFollowUps

        return {
          ...nextFollowUp,
          tooltip: `Mensagem: ${nextFollowUp.message}`,
          title: `${nextFollowUp.title} agendado para ${format(
            parseISO(nextFollowUp.dueDate),
            'dd/MM'
          )}`
        }
      }

      return undefined
    },
    hasTags() {
      return (this.healthTags.length > 0 ||
        this.currentChannel.demands?.length > 0) &&
        !this.showSearchMessage
        ? true
        : null
    },
    showMessages() {
      return this.$route.query.channelId === this.currentChannel.id
    },
    shouldShowCallout() {
      return (
        !this.isCalloutClosed ||
        this.currentChannel.screeningNavigation?.hasProtocol
      )
    },
    calloutMessage() {
      return this.currentChannel.screeningNavigation?.status ===
        screeningNavigationStatus.DROPPED
        ? {
            title: 'Triagem abandonada pelo membro',
            description:
              'O membro desistiu de responder o protocolo completo. Inicie o registro clínico para consultar o que foi respondido.'
          }
        : {
            title: 'Triagem com protocolo aplicado',
            description:
              'Inicie o registro clínico para consultar o caminho percorrido pelo membro na triagem.'
          }
    }
  },
  watch: {
    'currentChannel.status'(status) {
      if (status === ChannelStatus.MERGED && this.currentChannel.mergedWith) {
        if (this.$route.query.channelId === this.currentChannel.mergedWith) {
          return
        }
        this.$store.commit('ui/channel/resetChannelsUI')
        this.$router.push(
          `/member/${this.currentChannel.personId}/pillars?channelId=${this.currentChannel.mergedWith}`
        )
      }
    }
  },
  methods: {
    changeChannel(isPrivate) {
      this.$store.dispatch('pillars/channel/message/setIsPrivate', isPrivate)
    },
    focusMessages() {
      if (this.$refs.messages) this.$refs.messages.shouldFocusMessages = true

      setTimeout(() => {
        if (this.$refs.messages) this.$refs.messages.shouldFocusMessages = false
      }, 2000)
    },
    startVideocall() {
      window.open(`/channels/${this.currentChannel.id}/videocall`, '_blank')
    },
    async closeChannel() {
      this.isClosingChannel = true
      const response = await this.$store.dispatch(
        'pillars/channel/dischargeChat',
        this.currentChannel
      )
      if (response) {
        this.goToVirtualClinic()
      }
      this.isClosingChannel = false
    },
    handleCloseCallout(value) {
      this.isCalloutClosed = value
    },
    goToVirtualClinic() {
      this.$router.push('/virtualclinic')
    }
  }
}
</script>

<style lang="scss">
@import '@/style/_non-rendering';

:root,
[data-theme='on-light'] {
  --channel-background-color: var(--gl-color-shades-white);
  --message-staff-background: var(--sys-color-surface-background-light);
  --message-other-background: var(--gl-color-shades-gray-10);
  --message-staff-color: var(--sys-color-content-primary);
  --message-staff-border-color: var(--sys-color-stroke-active);
  --split-bar-background-color: var(--channel-background-color);
  --split-bar-background: var(--channel-background-color);
  --split-bar-color: var(--gl-color-shades-gray-60);
  --split-bar-border-color: var(--gl-color-shades-gray-20);
  --message-visualization-color: var(--gl-color-shades-gray-60);
}

.error {
  width: 100%;
  height: 100%;

  .message {
    margin-top: 50%;
    text-align: center;
  }
}

.chat-public {
  box-shadow: var(--gl-shadow-level-04);
  height: calc(100% - var(--filter-header-height));
  font-feature-settings: 'kern' off;
  position: relative;
  display: flex;
  flex-direction: column;

  &[data-has-tags] {
    height: calc(100% - 95px);
  }

  .chat {
    &__messages {
      overflow-y: scroll;
      flex-basis: 100%;
      overflow-x: hidden;
    }
  }
}

.chat-private {
  @extend .chat-public;

  --channel-background-color: var(--gl-color-shades-gray-90);
  --message-staff-background: var(--gl-color-shades-gray-80);
  --message-staff-color: var(--gl-color-shades-gray-00);
  --message-staff-border-color: var(--gl-color-shades-gray-80);
  --split-bar-background-color: var(--channel-background-color);
  --split-bar-background: var(--channel-background-color);
  --split-bar-border-color: var(--gl-color-shades-gray-60);
  --split-bar-color: var(--gl-color-shades-gray-20);
  --message-visualization-color: var(--gl-color-shades-gray-20);
  --message-other-background: var(--message-staff-background);

  .chat {
    &__write-box {
      --buttons-background-color: var(--gl-color-shades-gray-100);
      --button-background-active: var(--gl-color-shades-gray-90);
      --default-contrast-color: var(--gl-color-shades-gray-20);
      --default-contrast-brand-color: var(--sys-color-content-active);

      /** color for rich text **/
      --background-color: var(--sys-color-surface-background-dark);
      --background-disabled-color: var(--gl-color-shades-gray-80);
      --text-color: var(--gl-color-shades-white);
      --border-color: var(--gl-color-shades-gray-60);
      --border-focus-color: var(--gl-color-shades-white);
      --icon-color: var(--gl-color-shades-white);
    }
  }
}

.chat {
  @extend .chat-public;

  background-color: var(--channel-background-color);

  &__container {
    display: grid;
    grid-template-rows: 1fr auto;
    height: 100%;

    &--callout {
      margin: var(--gl-spacing-06);
    }
  }
}

.chat-messages__next-follow-up {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
